import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, History, Info } from "lucide-react";

import { api, formatApiError } from "../lib/api";
import { formatLocalHHMM, localHHMMToUtcIso } from "../lib/domain";
import { useAuth } from "../contexts/AuthContext";
import { isAttendanceOfficer } from "../lib/rbac";
import { Lock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import ModuleIdChip from "./ModuleIdChip";
import ProjectPicker from "./ProjectPicker";

const STATUS_OPTIONS = [
    { value: "present", label: "Present" },
    { value: "late", label: "Late" },
    { value: "absent", label: "Absent" },
    { value: "partial", label: "Partial / Half-day" },
    { value: "exception", label: "Exception" },
    { value: "paid_leave", label: "Paid Leave" },
    { value: "unpaid_leave", label: "Unpaid Leave" },
    { value: "weekend", label: "Weekend" },
    { value: "holiday", label: "Holiday" },
];

function isoToHM(iso) {
    // Iter60.p9 — was `getUTCHours()/getUTCMinutes()` which showed UTC
    // clock time while the Attendance table renders LOCAL clock time.
    // In Somalia (UTC+3) that made a "05:00" appear in the modal for
    // a row the table (correctly) rendered as "08:00", causing users
    // to overwrite the record with a 3-hour offset. See lib/domain.js.
    return formatLocalHHMM(iso);
}

function fmtDT(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
}

function fmtChangeValue(v) {
    // Iter48b — defensive: audit changes can carry any JSON-serialisable value
    // (bools, ints, ISO strings, nulls, and — for approved-revert entries —
    // datetime strings originally set by the seeder). Never let a bad shape
    // crash the render.
    try {
        if (v === null || v === undefined || v === "") return "—";
        if (typeof v === "boolean") return v ? "yes" : "no";
        if (typeof v === "number") return String(v);
        if (typeof v === "object") return JSON.stringify(v);
        const s = String(v);
        // ISO timestamp — try to pretty-print but fall back to raw string.
        if (s.length >= 16 && /^\d{4}-\d{2}-\d{2}T/.test(s)) {
            const d = new Date(s);
            if (!Number.isNaN(d.getTime())) return fmtDT(s);
        }
        return s;
    } catch (err) {
        // Log once so future contributors can spot regressions in DevTools.
        // eslint-disable-next-line no-console
        console.warn("[EditAttendanceDialog] fmtChangeValue crash for value:", v, err);
        return "(unrenderable)";
    }
}

// Iter48b — Minimal error boundary around the History tab so a single bad
// audit row can't nuke the whole modal. Renders a small "Something went
// wrong" panel with the raw error message + still lets the user use Edit tab.
class HistoryErrorBoundary extends React.Component {
    constructor(props) { super(props); this.state = { err: null }; }
    static getDerivedStateFromError(err) { return { err }; }
    componentDidCatch(err, info) {
        // eslint-disable-next-line no-console
        console.error("[EditAttendanceDialog] history render error:", err, info);
    }
    render() {
        if (this.state.err) {
            return (
                <div className="p-4 rounded-md border border-rose-200 bg-rose-50 text-xs text-rose-900" data-testid="edit-att-history-error">
                    <div className="font-semibold mb-1">History unavailable</div>
                    <div>Could not render the audit trail for this row. Edit still works.</div>
                    <div className="mt-2 mono text-[10px] text-rose-700 opacity-70">{String(this.state.err?.message || this.state.err)}</div>
                </div>
            );
        }
        return this.props.children;
    }
}

/**
 * EditAttendanceDialog — Iter48
 * Single-row edit of an attendance daily. All 8 fields editable. Shows a
 * "Manually adjusted" chip + biometric original preview when the row was
 * biometric-sourced. Warns before reverting an approved row to Pending.
 */
export default function EditAttendanceDialog({ open, onOpenChange, row, onSaved, adjustments = null }) {
    const { user } = useAuth();
    const [projects, setProjects] = useState([]);
    // Iter60.p35 Part A — Multi-project split (IC + CN only). Kept as its
    // own state array because the payload contract is different from the
    // legacy single project_id field.
    const [moduleCode, setModuleCode] = useState("");
    const [allocations, setAllocations] = useState([]);
    const [origAllocationsJson, setOrigAllocationsJson] = useState("[]");
    const [form, setForm] = useState({
        status: "",
        check_in_time: "",
        check_out_time: "",
        project_id: "",
        late_minutes: "",
        ot_hours: "",
        pay_multiplier: "",
        notes: "",
        // Iter53 — Excused early departure.
        excused_early_departure: false,
        excuse_note: "",
    });
    // Iter67 F2 — Attendance Officer field lock state.
    // Officer role has read-only access to `check_in_time`, `late_minutes`,
    // `ot_hours`, `pay_multiplier` — always. `check_out_time` is editable
    // ONLY when the row is missing a check-out (forgot-check-out flow);
    // once populated, it locks again.
    const officerScope = isAttendanceOfficer(user);
    const rowHasCheckOut = !!row?.last_clock_out;
    const officerCanEditCheckOut = officerScope && !rowHasCheckOut;
    const isLocked = (fieldKey) => {
        if (!officerScope) return false;
        if (fieldKey === "check_out_time") return rowHasCheckOut;
        return ["check_in_time", "late_minutes", "ot_hours", "pay_multiplier"].includes(fieldKey);
    };
    const [audit, setAudit] = useState(null);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("edit");

    const isBiometric = useMemo(() => {
        if (!row) return false;
        if (row.source_override === "manual") return false;
        const cls = [row.first_clock_in_classified_by, row.last_clock_out_classified_by].filter(Boolean);
        return cls.some((c) => c && c !== "manual");
    }, [row]);
    const isApproved = !!row?.approved;
    const hasOverride = row?.source_override === "manual";

    useEffect(() => {
        if (!open || !row) return;
        setTab("edit");
        setForm({
            status: row.status || "",
            check_in_time: isoToHM(row.first_clock_in),
            check_out_time: isoToHM(row.last_clock_out),
            project_id: row.project_id || "",
            late_minutes: row.late_minutes ?? "",
            ot_hours: row.overtime_minutes != null ? (row.overtime_minutes / 60).toFixed(2).replace(/\.00$/, "") : "",
            pay_multiplier: row.multiplier_applied != null ? String(row.multiplier_applied) : "",
            notes: row.notes || "",
            excused_early_departure: !!row.excused_early_departure,
            excuse_note: row.excuse_note || "",
        });
        // Fetch projects scoped to this row's module
        api.get("/projects", { params: { module_id: row.module_id } })
            .then(({ data }) => setProjects(Array.isArray(data) ? data : (data?.items || [])))
            .catch(() => setProjects([]));
        // Iter60.p35 Part A — Hydrate module code so the Project Split
        // section can hide itself on AP rows without a hardcoded id.
        api.get(`/modules`)
            .then(({ data }) => {
                const arr = Array.isArray(data) ? data : (data?.items || []);
                const m = arr.find((x) => x.id === row.module_id);
                setModuleCode((m?.code || "").toUpperCase());
            })
            .catch(() => setModuleCode(""));
        // Iter60.p35 Part A — Seed the Project Split state from the row's
        // stored allocations (may be null → empty). We keep a JSON
        // snapshot of the original so the diff on save is simple.
        const initAllocs = Array.isArray(row.project_allocations)
            ? row.project_allocations.map((a) => ({
                project_id: a.project_id || "",
                minutes: Number(a.minutes) || 0,
                note: a.note || "",
            }))
            : [];
        setAllocations(initAllocs);
        setOrigAllocationsJson(JSON.stringify(initAllocs));
        // Load audit history
        api.get(`/attendance/daily/${row.id}/audit`)
            .then(({ data }) => setAudit(data))
            .catch(() => setAudit(null));
    }, [open, row]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const buildPatch = () => {
        // Diff against the row's canonical values so we only send changed fields.
        const patch = {};
        const original = {
            status: row.status || "",
            check_in_time: isoToHM(row.first_clock_in),
            check_out_time: isoToHM(row.last_clock_out),
            project_id: row.project_id || "",
            late_minutes: row.late_minutes ?? "",
            ot_hours: row.overtime_minutes != null ? (row.overtime_minutes / 60).toFixed(2).replace(/\.00$/, "") : "",
            pay_multiplier: row.multiplier_applied != null ? String(row.multiplier_applied) : "",
            notes: row.notes || "",
        };
        if (form.status !== original.status) patch.status = form.status;
        // Iter60.p9 — the modal shows/edits LOCAL HH:MM (browser tz)
        // but the backend stores UTC. Convert local HH:MM → UTC ISO on
        // the way out. Backend `_combine_time` accepts ISO and
        // normalises it to UTC. Sending null clears the field.
        if (form.check_in_time !== original.check_in_time) {
            patch.check_in_time = form.check_in_time
                ? localHHMMToUtcIso(row.work_date, form.check_in_time)
                : null;
        }
        if (form.check_out_time !== original.check_out_time) {
            patch.check_out_time = form.check_out_time
                ? localHHMMToUtcIso(row.work_date, form.check_out_time)
                : null;
        }
        if (form.project_id !== original.project_id) patch.project_id = form.project_id || null;
        if (String(form.late_minutes) !== String(original.late_minutes)) {
            patch.late_minutes = form.late_minutes === "" ? null : Number(form.late_minutes);
        }
        if (String(form.ot_hours) !== String(original.ot_hours)) {
            patch.ot_hours = form.ot_hours === "" ? null : Number(form.ot_hours);
        }
        if (String(form.pay_multiplier) !== String(original.pay_multiplier)) {
            patch.pay_multiplier = form.pay_multiplier === "" ? null : Number(form.pay_multiplier);
        }
        if (form.notes !== original.notes) patch.notes = form.notes;
        // Iter53 — Excused early departure diff.
        if (!!form.excused_early_departure !== !!row.excused_early_departure) {
            patch.excused_early_departure = !!form.excused_early_departure;
        }
        if ((form.excuse_note || "") !== (row.excuse_note || "")) {
            patch.excuse_note = form.excuse_note || null;
        }
        // Iter60.p35 Part A — Diff project_allocations. Only send when
        // the shape or values changed. Blank / no-project rows are
        // filtered so a partially-filled row can't slip through.
        const cleaned = allocations
            .filter((a) => a.project_id && Number(a.minutes) > 0)
            .map((a) => ({
                project_id: a.project_id,
                minutes: Math.max(0, Math.round(Number(a.minutes) || 0)),
                note: (a.note || "").trim() || null,
            }));
        const nextJson = JSON.stringify(cleaned);
        if (nextJson !== origAllocationsJson) {
            patch.project_allocations = cleaned;
        }
        return patch;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const patch = buildPatch();
        if (Object.keys(patch).length === 0) {
            toast.info("No changes to save");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.patch(`/attendance/daily/${row.id}`, patch);
            toast.success(`Attendance updated${data.approved === false && isApproved ? " · reverted to Pending" : ""}`);
            onOpenChange(false);
            onSaved && onSaved(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    if (!row) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {/* Iter61 Phase A — Cap height + inner scroll. Header + footer
                remain visible; only the body (tabs + fields) scrolls so
                Save/Cancel are always reachable on 1366×768 laptops. */}
            <DialogContent
                className="max-w-2xl max-h-[85vh] p-0 flex flex-col overflow-hidden"
                data-testid="edit-attendance-dialog"
            >
                <DialogHeader className="p-6 pb-3 border-b flex-shrink-0" style={{ borderColor: "var(--asl-border)" }}>
                    <DialogTitle className="flex items-center gap-2">
                        Edit attendance
                        {hasOverride && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider rounded bg-amber-100 text-amber-800" data-testid="edit-att-manual-chip">
                                <Info className="h-3 w-3" /> Manually adjusted
                            </span>
                        )}
                        {isBiometric && !hasOverride && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider rounded bg-emerald-100 text-emerald-800">
                                Biometric-sourced
                            </span>
                        )}
                    </DialogTitle>
                    <div className="text-xs text-[color:var(--asl-muted)] mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                        {/* Iter60.p52 — surface the Module ID as a crimson pill
                            in the dialog header so the operator sees who they
                            are editing without opening a second tab. Falls
                            back gracefully to `—` when the row has no ID. */}
                        <ModuleIdChip
                            value={row.module_employee_id}
                            testid="edit-att-header-module-id"
                        />
                        {row.employee_name && (
                            <span className="asl-navy-text font-medium">
                                {row.employee_name}
                            </span>
                        )}
                        <span>·</span>
                        <span>{row.work_date}</span>
                        <span>·</span>
                        <span>edit count: {row.edit_count || 0}</span>
                        {row.last_edited_by_name ? (
                            <>
                                <span>·</span>
                                <span>last edited by {row.last_edited_by_name}</span>
                            </>
                        ) : null}
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="edit-att-body-scroll">
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList data-testid="edit-att-tabs">
                        <TabsTrigger value="edit" data-testid="edit-att-tab-edit">Edit</TabsTrigger>
                        <TabsTrigger value="history" data-testid="edit-att-tab-history">
                            <History className="h-3 w-3 mr-1" /> History{audit?.edit_count ? ` (${audit.edit_count})` : ""}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="edit" className="space-y-3">
                        {(isBiometric || hasOverride) && (audit?.biometric_in_original || audit?.biometric_out_original || row.first_clock_in_classified_by === "auto") && (
                            <div className="rounded-md border bg-emerald-50/60 border-emerald-200 p-2.5 text-xs" data-testid="edit-att-biometric-original">
                                <div className="font-semibold text-emerald-900 mb-1">Original biometric</div>
                                <div className="text-emerald-800 mono">
                                    IN: {fmtDT(audit?.biometric_in_original || row.first_clock_in)}
                                    <span className="mx-2 text-emerald-400">·</span>
                                    OUT: {fmtDT(audit?.biometric_out_original || row.last_clock_out)}
                                </div>
                            </div>
                        )}
                        {isApproved && (
                            <div className="rounded-md border bg-rose-50 border-rose-200 p-2.5 text-xs flex items-start gap-2" data-testid="edit-att-approved-warning">
                                <AlertTriangle className="h-4 w-4 text-rose-600 flex-shrink-0 mt-0.5" />
                                <div>
                                    <div className="font-semibold text-rose-900">This row is approved.</div>
                                    <div className="text-rose-800">Saving will revert it to Pending and require re-approval.</div>
                                </div>
                            </div>
                        )}

                        <form onSubmit={onSubmit} className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Status</Label>
                                <Select value={form.status || undefined} onValueChange={(v) => set("status", v)}>
                                    <SelectTrigger data-testid="edit-att-status"><SelectValue placeholder="—" /></SelectTrigger>
                                    <SelectContent>
                                        {STATUS_OPTIONS.map((s) => (
                                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Project</Label>
                                {/* Iter67 F1 — Universal ProjectPicker with
                                    Job # + name search. `allowNone` renders
                                    the fixed "— No project —" option at the
                                    top of the list. */}
                                <ProjectPicker
                                    projects={(projects || []).filter((p) => p.active !== false)}
                                    value={form.project_id || ""}
                                    onValueChange={(v) => set("project_id", v || "")}
                                    allowNone
                                    testid="edit-att-project"
                                />
                            </div>
                            <div>
                                <Label className="flex items-center gap-1.5">
                                    Check-in (HH:MM)
                                    {isLocked("check_in_time") && (
                                        <LockedFieldChip testid="edit-att-check-in-locked" />
                                    )}
                                </Label>
                                <Input
                                    type="time"
                                    value={form.check_in_time}
                                    onChange={(e) => set("check_in_time", e.target.value)}
                                    disabled={isLocked("check_in_time")}
                                    className={isLocked("check_in_time") ? "opacity-60 cursor-not-allowed" : ""}
                                    data-testid="edit-att-check-in"
                                />
                            </div>
                            <div>
                                <Label className="flex items-center gap-1.5">
                                    Check-out (HH:MM)
                                    {isLocked("check_out_time") && (
                                        <LockedFieldChip testid="edit-att-check-out-locked" />
                                    )}
                                </Label>
                                <Input
                                    type="time"
                                    value={form.check_out_time}
                                    onChange={(e) => set("check_out_time", e.target.value)}
                                    disabled={isLocked("check_out_time")}
                                    className={isLocked("check_out_time") ? "opacity-60 cursor-not-allowed" : ""}
                                    data-testid="edit-att-check-out"
                                />
                                {officerCanEditCheckOut && (
                                    <p
                                        className="text-[10px] text-amber-700 mt-1 leading-tight"
                                        data-testid="edit-att-check-out-forgot-hint"
                                    >
                                        Complete missing check-out (auto-detected forgot-check-out).
                                        Locks after save.
                                    </p>
                                )}
                            </div>
                            <div>
                                <Label className="flex items-center gap-1.5">
                                    Late minutes
                                    {isLocked("late_minutes") && (
                                        <LockedFieldChip testid="edit-att-late-locked" />
                                    )}
                                </Label>
                                <Input
                                    type="number" min="0" step="1"
                                    value={form.late_minutes}
                                    onChange={(e) => set("late_minutes", e.target.value)}
                                    disabled={isLocked("late_minutes")}
                                    className={isLocked("late_minutes") ? "opacity-60 cursor-not-allowed" : ""}
                                    data-testid="edit-att-late"
                                />
                            </div>
                            <div>
                                <Label className="flex items-center gap-1.5">
                                    OT hours
                                    {isLocked("ot_hours") && (
                                        <LockedFieldChip testid="edit-att-ot-locked" />
                                    )}
                                </Label>
                                <Input
                                    type="number" min="0" step="0.25"
                                    value={form.ot_hours}
                                    onChange={(e) => set("ot_hours", e.target.value)}
                                    disabled={isLocked("ot_hours")}
                                    className={isLocked("ot_hours") ? "opacity-60 cursor-not-allowed" : ""}
                                    data-testid="edit-att-ot"
                                />
                            </div>
                            <div>
                                <Label className="flex items-center gap-1.5">
                                    Pay multiplier
                                    {isLocked("pay_multiplier") && (
                                        <LockedFieldChip testid="edit-att-mult-locked" />
                                    )}
                                </Label>
                                <Input
                                    type="number" min="0" step="0.05"
                                    value={form.pay_multiplier}
                                    onChange={(e) => set("pay_multiplier", e.target.value)}
                                    disabled={isLocked("pay_multiplier")}
                                    className={isLocked("pay_multiplier") ? "opacity-60 cursor-not-allowed" : ""}
                                    data-testid="edit-att-mult"
                                />
                            </div>
                            <div className="col-span-2">
                                <Label>Notes</Label>
                                <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="edit-att-notes" />
                            </div>
                            {/* Iter53 — Excused early departure section */}
                            <div className="col-span-2 rounded-md border bg-amber-50/50 border-amber-200 p-3 space-y-2">
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={!!form.excused_early_departure}
                                        onChange={(e) => set("excused_early_departure", e.target.checked)}
                                        className="mt-1"
                                        data-testid="edit-att-excused-early-departure"
                                    />
                                    <div className="text-sm">
                                        <div className="font-medium asl-navy-text">Mark early departure as excused</div>
                                        <div className="text-xs text-[color:var(--asl-muted)]">
                                            When checked, payroll will skip the early-departure deduction for this day.
                                        </div>
                                    </div>
                                </label>
                                {form.excused_early_departure && (
                                    <div>
                                        <Label>Excuse note (optional)</Label>
                                        <Textarea
                                            rows={2}
                                            value={form.excuse_note}
                                            onChange={(e) => set("excuse_note", e.target.value)}
                                            placeholder="e.g. Medical appointment, family emergency, approved by manager"
                                            data-testid="edit-att-excuse-note"
                                        />
                                    </div>
                                )}
                            </div>
                            {/* Iter60.p21 — Adjustments section (Construction only, casual + non-casual). */}
                            {adjustments && (
                                <AdjustmentsSection
                                    row={row}
                                    onAdd={adjustments.onAdd}
                                    onRemove={adjustments.onRemove}
                                />
                            )}
                            {/* Iter60.p35 Part A — Project Split section
                                (IC + CN only). Hidden on AP because AP
                                runs a daily-wage flat cost model. */}
                            {moduleCode && moduleCode !== "AP" && (
                                <ProjectSplitSection
                                    row={row}
                                    projects={projects}
                                    allocations={allocations}
                                    onChange={setAllocations}
                                />
                            )}
                        </form>
                    </TabsContent>

                    <TabsContent value="history" data-testid="edit-att-history-panel">
                        <HistoryErrorBoundary>
                            {!audit && <div className="text-sm text-slate-500 p-4 text-center">Loading history…</div>}
                            {audit && audit.history?.length === 0 && (
                                <div className="text-sm text-slate-500 p-4 text-center italic">No edits recorded yet.</div>
                            )}
                            {audit && audit.history?.length > 0 && (
                                <div className="max-h-80 overflow-y-auto divide-y">
                                    {audit.history.map((h, idx) => (
                                        <div key={h.id || idx} className="py-2.5 px-1" data-testid={`edit-att-history-row-${idx}`}>
                                            <div className="flex items-baseline justify-between text-xs">
                                                <div>
                                                    <span className="font-semibold asl-navy-text">{h.edited_by_name}</span>
                                                    <span className="text-slate-500 ml-2">{fmtDT(h.edited_at)}</span>
                                                    <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] uppercase">{h.source}</span>
                                                </div>
                                                <span className="text-slate-400 text-[10px]">{h.changes?.length || 0} change{h.changes?.length === 1 ? "" : "s"}</span>
                                            </div>
                                            <ul className="mt-1 text-xs space-y-0.5">
                                                {(h.changes || []).map((c, ci) => (
                                                    <li key={ci} className="flex gap-2">
                                                        <span className="text-slate-500 min-w-[110px]">{c.field}</span>
                                                        <span className="mono text-slate-700 truncate">
                                                            {fmtChangeValue(c.old_value)} → <strong>{fmtChangeValue(c.new_value)}</strong>
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </HistoryErrorBoundary>
                    </TabsContent>
                </Tabs>
                </div>

                <DialogFooter className="p-4 border-t flex-shrink-0" style={{ borderColor: "var(--asl-border)" }}>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-att-cancel">Cancel</Button>
                    <Button onClick={onSubmit} disabled={saving} data-testid="edit-att-save">
                        {saving ? "Saving…" : (isApproved ? "Save & revert to Pending" : "Save changes")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// =========================================================================
// Iter60.p21 — Adjustments section inside EditAttendanceDialog.
//
// Rendered ONLY when the parent passes an `adjustments` prop (i.e. the
// row's employee is in Construction + the user can adjust). The
// parent owns the eligibility rule; this component is dumb.
// =========================================================================
function AdjustmentsSection({ row, onAdd, onRemove }) {
    const adjs = row?.adjustments || [];
    const total = adjs.reduce((s, a) => s + (Number(a.minutes) || 0), 0);
    const payable = Math.max(0, (Number(row?.worked_minutes) || 0) + total);
    return (
        <div
            className="rounded-lg border border-[color:var(--asl-border)] p-3 space-y-2 bg-white"
            data-testid="edit-att-adjustments-section"
        >
            <div className="flex items-baseline justify-between">
                <div>
                    <div className="text-sm font-semibold asl-navy-text">Hour adjustments</div>
                    <div className="text-[11px] text-[color:var(--asl-muted)]">
                        Construction — each entry is signed and included in payable time.
                    </div>
                </div>
                <div className="text-xs mono asl-navy-text">
                    total <span className={total >= 0 ? "text-emerald-700" : "text-[color:var(--asl-crimson)]"}>
                        {total >= 0 ? "+" : ""}{total}m
                    </span>
                    <span className="text-[color:var(--asl-muted)]"> · payable {payable}m</span>
                </div>
            </div>
            {adjs.length === 0 ? (
                <div className="text-xs text-[color:var(--asl-muted)] italic">No adjustments on this row yet.</div>
            ) : (
                <ul className="divide-y divide-[color:var(--asl-border)]">
                    {adjs.map((a) => {
                        const pos = Number(a.minutes) > 0;
                        return (
                            <li key={a.id} className="py-2 flex items-start gap-3" data-testid={`edit-att-adj-${a.id}`}>
                                <span className={`mono text-xs font-bold px-2 py-0.5 rounded-full ${
                                    pos ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-[color:var(--asl-crimson)]"
                                }`}>
                                    {pos ? "+" : ""}{a.minutes}m
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm asl-navy-text truncate">{a.note}</div>
                                    <div className="text-[11px] text-[color:var(--asl-muted)]">
                                        {a.applied_by_email || "system"}
                                        {a.applied_at ? ` · ${new Date(a.applied_at).toISOString().slice(0, 16).replace("T", " ")}` : ""}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => onRemove && onRemove(a)}
                                    className="text-xs text-[color:var(--asl-crimson)] hover:underline shrink-0"
                                    data-testid={`edit-att-adj-remove-${a.id}`}
                                >
                                    Remove
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
            <button
                type="button"
                onClick={onAdd}
                className="text-sm text-[color:var(--asl-crimson)] font-medium hover:underline"
                data-testid="edit-att-adj-add"
            >
                + Add adjustment
            </button>
        </div>
    );
}


// =========================================================================
// Iter60.p35 Part A — Multi-project time allocation
//
// Splits the row's worked minutes across N projects. Each entry is
// {project_id, minutes, note}. Validation:
//   * Sum(minutes) must be <= worked_minutes.
//   * Leftover (worked - sum) auto-goes to "No project" bucket in the
//     Cost by Project breakdown.
//   * AP module rows never see this section (daily-wage flat).
// =========================================================================
function ProjectSplitSection({ row, projects, allocations, onChange }) {
    const worked = Number(row?.worked_minutes) || 0;
    const allocSum = allocations.reduce((s, a) => s + (Number(a.minutes) || 0), 0);
    const leftover = worked - allocSum;
    const over = allocSum > worked;

    const activeProjects = (projects || []).filter((p) => p.active !== false);
    const setAt = (idx, patch) => {
        onChange(allocations.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
    };
    const remove = (idx) => onChange(allocations.filter((_, i) => i !== idx));
    const add = () => onChange([...allocations, { project_id: "", minutes: 0, note: "" }]);

    const fmt = (min) => {
        const h = Math.floor((min || 0) / 60);
        const m = (min || 0) % 60;
        return `${h}h ${String(m).padStart(2, "0")}m`;
    };

    return (
        <div
            className="col-span-2 rounded-lg border border-[color:var(--asl-border)] p-3 space-y-2 bg-white"
            data-testid="edit-att-project-split-section"
        >
            <div className="flex items-baseline justify-between">
                <div>
                    <div className="text-sm font-semibold asl-navy-text">Project Split</div>
                    <div className="text-[11px] text-[color:var(--asl-muted)]">
                        Optional. Split worked minutes across multiple projects. Any
                        unallocated minutes fall into "— No project —" in Cost by Project.
                    </div>
                </div>
                <div className="text-xs mono">
                    <span className={over ? "text-[color:var(--asl-crimson)] font-bold" : "asl-navy-text"}>
                        Allocated {fmt(allocSum)}
                    </span>
                    <span className="text-[color:var(--asl-muted)]"> / worked {fmt(worked)} · </span>
                    <span className={leftover < 0 ? "text-[color:var(--asl-crimson)] font-bold" : "asl-navy-text"}>
                        remaining {fmt(Math.max(0, leftover))}
                    </span>
                </div>
            </div>

            {over && (
                <div
                    className="rounded-md border bg-rose-50 border-rose-200 p-2 text-xs text-rose-800 flex items-start gap-2"
                    data-testid="edit-att-project-split-over-warning"
                >
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
                    Allocated minutes exceed worked_minutes. The server will reject this on save.
                </div>
            )}

            {allocations.length === 0 ? (
                <div className="text-xs text-[color:var(--asl-muted)] italic" data-testid="edit-att-project-split-empty">
                    No project splits — legacy single-project attribution applies.
                </div>
            ) : (
                <table className="w-full text-xs" data-testid="edit-att-project-split-table">
                    <thead className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                        <tr>
                            <th className="text-left px-1 py-1">Project</th>
                            <th className="text-right px-1 py-1 w-16">Hours</th>
                            <th className="text-right px-1 py-1 w-16">Minutes</th>
                            <th className="text-left px-1 py-1">Note</th>
                            <th className="w-6"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {allocations.map((a, idx) => {
                            const hours = Math.floor((Number(a.minutes) || 0) / 60);
                            const mins = (Number(a.minutes) || 0) % 60;
                            const onHours = (v) => {
                                const h = Math.max(0, Math.floor(Number(v) || 0));
                                setAt(idx, { minutes: h * 60 + mins });
                            };
                            const onMins = (v) => {
                                const m = Math.max(0, Math.min(59, Math.floor(Number(v) || 0)));
                                setAt(idx, { minutes: hours * 60 + m });
                            };
                            return (
                                <tr key={idx} className="border-t" style={{ borderColor: "var(--asl-border)" }}
                                    data-testid={`edit-att-project-split-row-${idx}`}>
                                    <td className="px-1 py-1">
                                        <Select
                                            value={a.project_id || undefined}
                                            onValueChange={(v) => setAt(idx, { project_id: v })}
                                        >
                                            <SelectTrigger className="h-8" data-testid={`edit-att-project-split-pid-${idx}`}>
                                                <SelectValue placeholder="Select project" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {activeProjects.map((p) => (
                                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    <td className="px-1 py-1">
                                        <Input
                                            type="number" min="0" step="1"
                                            className="h-8"
                                            value={hours}
                                            onChange={(e) => onHours(e.target.value)}
                                            data-testid={`edit-att-project-split-hours-${idx}`}
                                        />
                                    </td>
                                    <td className="px-1 py-1">
                                        <Input
                                            type="number" min="0" max="59" step="1"
                                            className="h-8"
                                            value={mins}
                                            onChange={(e) => onMins(e.target.value)}
                                            data-testid={`edit-att-project-split-mins-${idx}`}
                                        />
                                    </td>
                                    <td className="px-1 py-1">
                                        <Input
                                            className="h-8"
                                            placeholder="optional"
                                            value={a.note || ""}
                                            onChange={(e) => setAt(idx, { note: e.target.value })}
                                            data-testid={`edit-att-project-split-note-${idx}`}
                                        />
                                    </td>
                                    <td className="px-1 py-1">
                                        <button
                                            type="button"
                                            onClick={() => remove(idx)}
                                            className="text-xs text-[color:var(--asl-crimson)] hover:underline"
                                            data-testid={`edit-att-project-split-remove-${idx}`}
                                        >
                                            ✕
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            <button
                type="button"
                onClick={add}
                className="text-sm text-[color:var(--asl-crimson)] font-medium hover:underline"
                data-testid="edit-att-project-split-add"
            >
                + Add allocation
            </button>
        </div>
    );
}



/**
 * Iter67 F2 — Small padlock chip rendered next to the field Label when
 * the Attendance Officer viewing this modal cannot edit the field.
 * Hover / long-press surfaces the "Locked — requires Dept Manager"
 * tooltip so operators know who to talk to.
 */
function LockedFieldChip({ testid = "att-field-locked" }) {
    return (
        <TooltipProvider delayDuration={150}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span
                        className="inline-flex items-center gap-0.5 rounded-sm bg-slate-100 px-1 py-0.5 text-[9px] font-medium text-slate-600"
                        data-testid={testid}
                        aria-label="Locked — requires Dept Manager"
                    >
                        <Lock className="h-2.5 w-2.5" aria-hidden="true" />
                    </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-[11px]">
                    Locked — requires Dept Manager
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

